"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/apotek/ - tempat berkas INI berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./apotek                      -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { APOTEK, CATATAN, FOTO, LAYANAN, PESAN_WA, TERKAIT } from "./apotek";

/* ============================================================
   APOTEK - /layanan/apotek

   Sebelum berkas ini ada, alamat ini jatuh ke halaman 404 "Sedang
   Dalam Proses Pengembangan" - padahal sudah ditunjuk menu Header.

   Susunannya menyalin halaman Laboratorium: kepala dua kolom
   beserta foto, kartu kontak seukuran, daftar layanan, catatan,
   lalu galeri. Sengaja tidak dibuat lebih pintar dari itu - halaman
   yang berperilaku sama lebih mudah dirawat daripada halaman yang
   masing-masing punya kejutan sendiri.

   ============================================================
   BEDA SATU-SATUNYA DARI HALAMAN LABORATORIUM: SUMBER GAMBAR

   Laboratorium memakai `import foto1 from "./images/1.jpg"` -
   gambarnya duduk di dalam folder halaman itu sendiri.

   Halaman ini memakai berkas dari public/images/layanan/. Dua
   akibatnya, dan keduanya sudah ditangani di bawah:

     1. Ukuran gambar harus disebut manual (lihat `lebar` dan
        `tinggi` di apotek.js). Next.js tidak membaca berkas di
        public/ saat build, jadi ia tidak tahu ukurannya.
     2. Tidak ada efek blur saat gambar dimuat. `placeholder="blur"`
        butuh data yang hanya dihasilkan lewat import. Diganti warna
        latar merah muda, jadi bingkainya tidak berkedip putih.

   Kalau suatu saat mau menyeragamkan dengan halaman lain: pindahkan
   gambarnya ke app/layanan/apotek/images/, ganti isi FOTO jadi hasil
   import, hapus `lebar`/`tinggi`, lalu kembalikan placeholder="blur".

   ============================================================
   SEMUA GRID MEMAKAI minmax(min(...px, 100%), 1fr)

   Jangan disederhanakan jadi minmax(...px, 1fr). Kolom dengan
   minimum 330px menolak menyusut di bawah 330px, padahal isi halaman
   di ponsel 360px hanya selebar 328px - selisihnya membuat seluruh
   halaman bisa digeser ke samping.

   ============================================================
   className ditulis satu baris - lihat peringatan hydration di
   Facility.jsx. Jangan pakai string multi-baris di berkas ini.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Sama nilainya dengan IgdContent, MCUContent, LaboratoriumContent,
   dan SwabPcrContent. app/layanan/page.jsx sudah ada, jadi remah roti
   boleh menautkannya. Kalau salah satu halaman diubah, ubah semuanya
   supaya perilakunya tidak berbeda-beda. */
const TAUTKAN_INDUK = true;

/* ---------- SAKLAR SPANDUK MODE PENGEMBANGAN ----------
   false = spanduk merah catatan data disembunyikan.
   Ubah ke true untuk melihat lagi daftar data yang belum diisi.
   Di produksi spanduk ini tetap tidak pernah tayang, apa pun isinya. */
const TAMPILKAN_CATATAN_DEV = false;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Apotek", en: "Pharmacy" },

  /* Ditulis seumum ini dengan sengaja. Segala yang lebih rinci -
     jam buka, jenis resep yang diterima, cara pembayaran - belum
     pernah dikonfirmasi, dan kalimat pemasaran yang enak dibaca
     tidak sepadan dengan risiko orang datang membawa harapan yang
     salah. Perbarui setelah bagian farmasi menjawab. */
  sub: {
    id: "Apotek RS Jakarta Medical Center. Hubungi customer service untuk menanyakan ketersediaan obat, jam pelayanan, dan cara penebusan resep.",
    en: "The pharmacy at RS Jakarta Medical Center. Contact customer service to ask about medicine availability, service hours and how to redeem a prescription.",
  },

  chatWa: { id: "Chat lewat WhatsApp", en: "Chat on WhatsApp" },
  teleponSekarang: { id: "Telepon sekarang", en: "Call now" },
  lihatPeta: { id: "Buka di Google Maps", en: "Open in Google Maps" },
  alamat: { id: "Alamat", en: "Address" },

  layanan: { id: "Pilihan Layanan", en: "Available Services" },
  hubungi: { id: "Hubungi kami", en: "Contact us" },

  catatan: { id: "Yang Perlu Diketahui", en: "Good to Know" },

  terkait: { id: "Layanan Terkait", en: "Related Services" },
  buka: { id: "Buka halaman", en: "Open page" },

  foto: { id: "Apotek", en: "The Pharmacy" },
  fotoSub: { id: "Klik untuk memperbesar.", en: "Click to enlarge." },
  perbesar: { id: "Perbesar foto", en: "Enlarge photo" },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Sebelumnya", en: "Previous" },
  berikutnya: { id: "Berikutnya", en: "Next" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

function rupiah(nilai) {
  if (nilai == null) return null;
  return `Rp ${new Intl.NumberFormat("id-ID").format(nilai)}`;
}

export default function ApotekContent() {
  const { lang, t } = useLanguage();
  const [pratinjau, setPratinjau] = useState(null);

  const utama = APOTEK.telepon.find((n) => n.utama) ?? APOTEK.telepon[0];
  const peta = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(APOTEK.alamat.cari)}`;

  const fotoUtama = FOTO[0] ?? null;
  const fotoSisanya = FOTO.slice(1);

  /* Nomor dengan medan `wa` membuka WhatsApp beserta pesan pembuka
     yang sudah terisi; sisanya membuka aplikasi telepon. */
  function tautanNomor(n) {
    if (!n.wa) return `tel:${n.tel}`;
    const pesan = pilihTeks(n.pesanWa ?? PESAN_WA, lang);
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pesan)}`;
  }

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA: DUA KOLOM ---------- */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "48px 16px 48px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 40, alignItems: "center" }}>

            <div style={{ minWidth: 0 }}>
              <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
                {pilihTeks(TEKS.eyebrow, lang)}
              </p>

              <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.4vw, 44px)" }}>
                {pilihTeks(TEKS.judul, lang)}
              </h1>

              {/* Lencana jam hanya dirender kalau jamnya sudah pasti -
                 lihat catatan di apotek.js. Jam apotek adalah
                 keterangan yang paling sering dicari di halaman
                 seperti ini, jadi begitu dikonfirmasi, isi duluan. */}
              {APOTEK.jamLayanan && (
                <p
                  className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 font-bold text-emerald-800"
                  style={{ marginTop: 16, borderRadius: 999, padding: "7px 14px", fontSize: 13.5 }}
                >
                  <IkonJam />
                  {pilihTeks(APOTEK.jamLayanan, lang)}
                </p>
              )}

              <p className="text-[var(--c-body)]" style={{ marginTop: 16, maxWidth: 520, fontSize: 15.5, lineHeight: 1.7 }}>
                {pilihTeks(TEKS.sub, lang)}
              </p>

              {/* Nomornya sendiri yang jadi tulisan tombol, bukan kata
                 "Hubungi kami". Orang yang membuka dari komputer perlu
                 MEMBACA angkanya untuk ditekan di ponsel lain. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 26 }}>
                <a
                  href={tautanNomor(utama)}
                  target={utama.wa ? "_blank" : undefined}
                  rel={utama.wa ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-3 rounded-[12px] bg-[var(--c-brand)] font-bold text-white shadow-sm transition-colors hover:bg-[var(--c-brand-strong)]"
                  style={{ padding: "16px 26px", fontSize: 19 }}
                >
                  {utama.wa ? <IkonWhatsApp /> : <IkonTelepon />}
                  {utama.tampil}
                </a>

                {utama.wa && (
                  <a
                    href={`tel:${utama.tel}`}
                    className="inline-flex items-center gap-2 rounded-[12px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
                    style={{ padding: "16px 22px", fontSize: 15 }}
                  >
                    <IkonTelepon />
                    {pilihTeks(TEKS.teleponSekarang, lang)}
                  </a>
                )}

                <a
                  href={peta}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-[12px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
                  style={{ padding: "16px 22px", fontSize: 15 }}
                >
                  <IkonPeta />
                  {pilihTeks(TEKS.lihatPeta, lang)}
                </a>
              </div>
            </div>

            {fotoUtama && (
              <button
                id="foto-apotek-0"
                type="button"
                onClick={() => setPratinjau(0)}
                aria-label={pilihTeks(TEKS.perbesar, lang)}
                className="group relative block w-full overflow-hidden shadow-sm transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                style={{ borderRadius: 16, aspectRatio: "4 / 3", backgroundColor: "#dff2e8" }}
              >
                {/* Tanpa placeholder="blur" - lihat catatan sumber
                   gambar di kepala berkas. Warna latar #dff2e8 di
                   bingkai atas yang menggantikannya. */}
                <Image
                  src={fotoUtama.gambar}
                  alt={pilihTeks(fotoUtama.alt, lang)}
                  fill
                  priority
                  sizes="(min-width: 1024px) 540px, 100vw"
                  className="transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
                  style={{ objectFit: "cover", objectPosition: "center" }}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 64px" }}>

        {/* ---------- SPANDUK MODE PENGEMBANGAN ----------
           Tidak ikut tayang di produksi, dan sekarang dimatikan juga
           saat pengembangan lewat TAMPILKAN_CATATAN_DEV di atas. */}
        {TAMPILKAN_CATATAN_DEV && process.env.NODE_ENV !== "production" && LAYANAN.length === 0 && (
          <div className="border-l-4 border-red-500 bg-red-50" style={{ marginBottom: 40, borderRadius: 8, padding: "14px 16px" }}>
            <p className="font-bold text-red-800" style={{ fontSize: 13.5 }}>
              Hanya tampil saat pengembangan - bukan bagian dari halaman
            </p>
            <ul className="text-red-700" style={{ marginTop: 6, fontSize: 13, listStyle: "disc", paddingLeft: 20 }}>
              <li>jenis layanan apotek belum diisi di apotek.js</li>
              <li>jam pelayanan apotek belum dikonfirmasi</li>
              <li>belum jelas apakah resep dari luar RS bisa ditebus</li>
              <li>belum jelas apakah menerima BPJS atau asuransi</li>
            </ul>
          </div>
        )}

        {/* ---------- KARTU KONTAK ----------
           Nomor utama tidak diulang di sini - ia sudah jadi tombol
           besar di kepala halaman. Disaring lewat `utama`, jadi kalau
           tandanya dipindah ke nomor lain, kartunya menyesuaikan
           sendiri. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 18 }}>

          <article className="border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "22px 20px" }}>
            <div className="text-[var(--c-brand)]" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <IkonPeta />
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                {pilihTeks(TEKS.alamat, lang)}
              </p>
            </div>

            <p className="text-slate-700" style={{ marginTop: 12, flex: "1 1 auto", fontSize: 14.5, lineHeight: 1.65 }}>
              {APOTEK.alamat.baris}
            </p>

            <a
              href={peta}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)]"
              style={{ marginTop: 14, fontSize: 13.5 }}
            >
              {pilihTeks(TEKS.lihatPeta, lang)}
            </a>
          </article>

          {APOTEK.telepon.filter((n) => !n.utama).map((n) => {
            const lewatWa = Boolean(n.wa);
            const href = tautanNomor(n);

            return (
              <article key={n.tel} className="border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "22px 20px" }}>
                <div className="text-[var(--c-brand)]" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {lewatWa ? <IkonWhatsApp /> : <IkonTelepon />}
                  <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                    {pilihTeks(n.label, lang)}
                  </p>
                </div>

                <p style={{ marginTop: 12, flex: "1 1 auto" }}>
                  <a
                    href={href}
                    target={lewatWa ? "_blank" : undefined}
                    rel={lewatWa ? "noopener noreferrer" : undefined}
                    className="font-bold text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]"
                    style={{ fontSize: 21 }}
                  >
                    {n.tampil}
                  </a>
                </p>

                <a
                  href={href}
                  target={lewatWa ? "_blank" : undefined}
                  rel={lewatWa ? "noopener noreferrer" : undefined}
                  className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)]"
                  style={{ marginTop: 14, fontSize: 13.5 }}
                >
                  {lewatWa ? pilihTeks(TEKS.chatWa, lang) : pilihTeks(TEKS.teleponSekarang, lang)}
                </a>
              </article>
            );
          })}
        </div>

        {/* ---------- JENIS LAYANAN ----------
           Muncul sendiri begitu LAYANAN di apotek.js diisi. */}
        {LAYANAN.length > 0 && (
          <>
            <h2 className="font-bold leading-tight text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 28 }}>
              {pilihTeks(TEKS.layanan, lang)}
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(330px, 100%), 1fr))", gap: 18, marginTop: 22 }}>
              {LAYANAN.map((p) => (
                <article key={p.kode} className="border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <Lencana>{p.kode}</Lencana>
                    <div style={{ minWidth: 0 }}>
                      <p className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 16.5 }}>
                        {pilihTeks(p.nama, lang)}
                      </p>
                      {p.julukan && (
                        <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-brand-mid)]" style={{ marginTop: 3, fontSize: 10.5 }}>
                          {pilihTeks(p.julukan, lang)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 16, flex: "1 1 auto" }}>
                    {rupiah(p.harga) ? (
                      <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 23 }}>
                        {rupiah(p.harga)}
                      </p>
                    ) : (
                      <p className="font-bold text-[var(--c-brand)]" style={{ fontSize: 18 }}>
                        {pilihTeks(TEKS.hubungi, lang)}
                      </p>
                    )}

                    {p.catatan && (
                      <p className="text-[var(--c-muted)]" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}>
                        {pilihTeks(p.catatan, lang)}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {/* ---------- CATATAN ---------- */}
        {CATATAN.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.catatan, lang)}
            </h2>
            <ul style={{ marginTop: 16, maxWidth: 720, display: "grid", gap: 10 }}>
              {CATATAN.map((c, i) => (
                <li key={i} className="text-slate-700" style={{ display: "flex", gap: 10, fontSize: 14.5, lineHeight: 1.7 }}>
                  <span aria-hidden="true" className="font-bold text-[var(--c-brand-mid)]">&bull;</span>
                  <span>{pilihTeks(c, lang)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* ---------- LAYANAN TERKAIT ----------
           Halaman apotek yang belum lengkap tetap punya nilai kalau ia
           mengantar orang ke halaman yang SUDAH lengkap. Dikosongkan
           di apotek.js kalau tautannya tidak berlaku lagi. */}
        {TERKAIT.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.terkait, lang)}
            </h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 18, marginTop: 18 }}>
              {TERKAIT.map((k) => (
                <Link
                  key={k.href}
                  href={k.href}
                  className="group border border-[var(--c-line)] bg-[var(--c-surface)] transition-colors hover:border-[var(--c-brand-line)] hover:bg-[var(--c-tint)]"
                  style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}
                >
                  <p className="font-bold text-[var(--c-ink)]" style={{ fontSize: 17 }}>
                    {pilihTeks(k.judul, lang)}
                  </p>
                  <p className="text-[var(--c-body)]" style={{ marginTop: 8, flex: "1 1 auto", fontSize: 14, lineHeight: 1.6 }}>
                    {pilihTeks(k.ringkas, lang)}
                  </p>
                  <span className="font-bold text-[var(--c-brand)] underline underline-offset-4" style={{ marginTop: 14, fontSize: 13.5 }}>
                    {pilihTeks(TEKS.buka, lang)} &rarr;
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* ---------- FOTO LAIN ----------
           Foto pertama sudah tampil di kepala halaman, jadi bagian ini
           hanya dirender kalau ada foto kedua dan seterusnya. */}
        {fotoSisanya.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.foto, lang)}
            </h2>
            <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 13.5 }}>
              {pilihTeks(TEKS.fotoSub, lang)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 16, marginTop: 16 }}>
              {fotoSisanya.map((f, i) => (
                <button
                  key={i + 1}
                  id={`foto-apotek-${i + 1}`}
                  type="button"
                  onClick={() => setPratinjau(i + 1)}
                  className="group relative block w-full overflow-hidden border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                  style={{ borderRadius: 12, aspectRatio: "4 / 3", backgroundColor: "#dff2e8" }}
                >
                  <Image
                    src={f.gambar}
                    alt={pilihTeks(f.alt, lang)}
                    fill
                    sizes="(min-width: 768px) 360px, 100vw"
                    className="transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
                    style={{ objectFit: "cover", objectPosition: "center" }}
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      <Footer />

      {pratinjau !== null && (
        <Pratinjau
          indeks={pratinjau}
          lang={lang}
          onTutup={() => setPratinjau(null)}
          onPindah={(i) => setPratinjau(i)}
        />
      )}
    </div>
  );
}

/* Lencana berbentuk pil, bukan lingkaran berukuran tetap: kodenya
   bisa "Darah" atau "Urine". Sama dengan halaman MCU dan Laboratorium. */
function Lencana({ children }) {
  return (
    <span
      className="font-bold text-white"
      style={{
        backgroundColor: "#0f6a3b",
        borderRadius: 8,
        padding: "5px 10px",
        fontSize: 12.5,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      }}
    >
      {children}
    </span>
  );
}

/* Ikon ditulis sebagai komponen kecil supaya dipakai berulang tanpa
   menyalin path SVG-nya. aria-hidden karena maknanya sudah disampaikan
   tulisan di sebelahnya. */
function IkonTelepon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M4.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 2.5 5.7 2 2 0 0 1 4.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IkonWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M3.5 20.5l1.3-4.2A8.2 8.2 0 1 1 8 19.3l-4.5 1.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 8.5c.3 2.5 2 4.2 4.5 4.5l.8-1.3 2 .8v1.3c0 .6-.5 1.1-1.1 1a7.8 7.8 0 0 1-7.2-7.2c0-.6.4-1.1 1-1.1h1.3l.8 2L9 8.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function IkonPeta() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function IkonJam() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 15, height: 15, flex: "0 0 auto" }}>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.5V12l3 1.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ============================================================
   PRATINJAU FOTO - pola yang sama dengan halaman IGD

   Ditutup dengan tombol silang, Esc, atau menekan latarnya. Gulir
   halaman dikunci selama terbuka, dan fokus papan ketik dikembalikan
   ke foto yang ditekan saat ditutup.
   ============================================================ */

function Pratinjau({ indeks, lang, onTutup, onPindah }) {
  const tombolTutup = useRef(null);
  const foto = FOTO[indeks];
  const banyak = FOTO.length > 1;

  useEffect(() => {
    tombolTutup.current?.focus();

    const gulirSemula = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const tekan = (e) => {
      if (e.key === "Escape") onTutup();
      if (!banyak) return;
      if (e.key === "ArrowRight") onPindah((indeks + 1) % FOTO.length);
      if (e.key === "ArrowLeft") onPindah((indeks - 1 + FOTO.length) % FOTO.length);
    };

    window.addEventListener("keydown", tekan);

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirSemula;
      document.getElementById(`foto-apotek-${indeks}`)?.focus();
    };
  }, [indeks, banyak, onTutup, onPindah]);

  if (!foto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pilihTeks(foto.judul, lang) || pilihTeks(TEKS.foto, lang)}
      onClick={onTutup}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(15, 4, 12, 0.86)", padding: 16 }}
    >
      {/* width: fit-content - kotaknya menyusut mengikuti gambar, jadi
         tidak ada bidang kosong di kiri-kanan. */}
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "fit-content", maxWidth: "100%" }}>
        {/* width & height WAJIB ada karena src-nya berupa alamat teks dari
           public/, bukan hasil import - Next.js tidak bisa membaca
           ukurannya sendiri. Angkanya diambil dari apotek.js. style
           width/height auto membuat browser tetap memakai perbandingan
           sisi gambar aslinya, jadi kalau angkanya meleset sedikit,
           gambarnya tidak ikut penyet. */}
        <Image
          src={foto.gambar}
          alt={pilihTeks(foto.alt, lang)}
          width={foto.lebar ?? 1600}
          height={foto.tinggi ?? 1200}
          sizes="90vw"
          className="rounded-lg"
          style={{ display: "block", width: "auto", height: "auto", maxWidth: "100%", maxHeight: "82vh" }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
          <p className="font-semibold text-white/80" style={{ fontSize: 13 }}>
            {pilihTeks(foto.judul, lang)}
          </p>

          {banyak && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="text-white/60" style={{ fontSize: 12.5 }}>
                {indeks + 1} / {FOTO.length}
              </span>
              <button
                type="button"
                onClick={() => onPindah((indeks - 1 + FOTO.length) % FOTO.length)}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &larr;
              </button>
              <button
                type="button"
                onClick={() => onPindah((indeks + 1) % FOTO.length)}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &rarr;
              </button>
            </div>
          )}
        </div>

        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label={pilihTeks(TEKS.tutup, lang)}
          className="bg-[var(--c-surface)] text-[var(--c-ink)] shadow-lg transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          style={{ position: "absolute", top: -14, right: -6, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 999 }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18 }}>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
